"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "@/components/fun-ui/Button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/fun-ui/Dialog"
import { useFetchPapers, useGlobalConfig, usePapers, useSummarizes, useUserVip, useWorkflows } from "@/stores/appStore";
import { SummarizeIcon } from "@/components/fun-ui/icons";
import { useRouter } from "next/navigation";
import { Paper } from "@/stores/slices/paperSlice";
import { useSubjectId } from "@/components/subjects/hooks/usePathParams";
import { UpgradeVipButton } from "@/components/payment/UpgradeVipButton";
import { useDialog } from "@/hooks/useDialog";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/fun-ui/Tip";


interface SummarizeCreatorProps extends React.HTMLAttributes<HTMLFormElement> {
  ids: string[];
  iconOnly: boolean;
}

export default function SummarizeCreator({ ids, iconOnly }: SummarizeCreatorProps) {
  // 最终选中的文献
  const [unabledPapers, setUnabledPapers] = useState<Paper[]>([]);
  const [openUnabledPapers, setOpenUnabledPapers] = useState(false);
  const { createErrorMsg, setCreateErrorMsg } = useSummarizes();
  const router = useRouter();
  const subjectId = useSubjectId();
  // 获取完整的列表
  const papers = usePapers('', 'base');
  const setErrorMsgOpen = useCallback((open: boolean) => {
    if (!open) {
      setCreateErrorMsg('');
    }
  }, [setCreateErrorMsg]);

  const {
    selectedIds,
    setSelectedIds,
  } = useWorkflows();

  const {
    fetchPapers,
  } = useFetchPapers();
  // 稳定 fetchPapers 引用，防止副作用依赖抖动
  const fetchPapersRef = useRef(fetchPapers);
  useEffect(() => { fetchPapersRef.current = fetchPapers; }, [fetchPapers]);
  // 防止 base 模式重复并发请求
  const fetchingBaseRef = useRef(false);

  useEffect(() => {
    const selectedPapers = papers.filter(({ paperId }) => {
      return ids.includes(paperId);
    });
    const unabledPapers = selectedPapers.filter(({ extractStatus = 0 }) => {
      return extractStatus < 0;
    });
    setUnabledPapers(unabledPapers);
    const selectedIds = selectedPapers.filter(({ extractStatus = 0 }) => {
      return extractStatus >= 0;
    }).map(({ paperId }) => paperId);
    setSelectedIds(selectedIds);
  }, [ids, papers, setUnabledPapers, setSelectedIds]);

  useEffect(() => {
    if (!papers.length && !fetchingBaseRef.current) {
      fetchingBaseRef.current = true;
      fetchPapersRef.current({
        subjectId,
        mode: 'base',
      }).finally(() => { fetchingBaseRef.current = false; });
    }
  }, [papers.length, subjectId]);

  const paperIds = useMemo(() => {
    return papers.map((paper) => paper.paperId);
  }, [papers]);

  useEffect(() => {
    const needRefresh = ids.some((id) => !paperIds.includes(id));
    if (needRefresh && !fetchingBaseRef.current) {
      fetchingBaseRef.current = true;
      fetchPapersRef.current({
        subjectId,
        mode: 'base',
      }).finally(() => { fetchingBaseRef.current = false; });
    }
  }, [ids, paperIds, subjectId]);

  const {
    value: config,
    fetch: fetchGlobalConfig,
  } = useGlobalConfig().globalConfig;

  useEffect(() => {
    fetchGlobalConfig();
  }, [fetchGlobalConfig]);

  const { vip } = useUserVip();
  const { alert } = useDialog();

  const maxSummaryCount = useMemo(() => {
    return (vip === 1 ? config?.maxVipSummaryCount : config?.maxSummaryCount) || 50
  }, [config?.maxSummaryCount, config?.maxVipSummaryCount, vip]);

  const label = 'AI 文献综述';
  return (
    <>
      <Tip label={iconOnly ? label : ''} asChild>
        <Button
          className={cn(
            "flex gap-1",
            iconOnly ? "w-10 p-0" : "min-w-[130px]"
          )}
          variant="outline"
          onClick={() => {
            if (maxSummaryCount) {
              if (ids?.length <= 0) {
                router.push(`/workflow-creator`);
              } else if (ids.length > maxSummaryCount && vip === 0) {
                alert({
                  title: '升级 Plus 会员可支持 AI Max「Deep」 模式',
                  content: `当前可导入最多 ${maxSummaryCount} 篇文献，升级 Plus  会员后使用 AI Max「Deep」 模式最多可导入 ${config?.maxVipSummaryCount} 篇文献。`,
                  cancelText: '重选',
                  ConfirmButton: <UpgradeVipButton />,
                });
              } else if (ids.length > maxSummaryCount && vip === 1) {
                toast.error(`暂仅支持 ${maxSummaryCount} 篇内文献生成综述`);
              } else if (unabledPapers.length) {
                setOpenUnabledPapers(true);
              } else {
                router.push(`/workflow-creator/selected`);
              }
            }
          }}>
          <SummarizeIcon />
          <span
            className={cn(
              iconOnly && "hidden"
            )}
          >{label}</span>
        </Button>
      </Tip>
      {
        createErrorMsg ? (
          <Dialog open={!!createErrorMsg} onOpenChange={setErrorMsgOpen}>
            <DialogContent className="w-[400px]">
              <DialogHeader>
                <DialogTitle>提示</DialogTitle>
                <DialogDescription>{createErrorMsg}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => {
                  setErrorMsgOpen(false);
                }}>确认</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog >
        ) : null
      }
      {
        unabledPapers.length ? (
          <Dialog open={openUnabledPapers}>
            <DialogContent className="w-[400px]">
              <DialogHeader>
                <DialogTitle>提示</DialogTitle>
              </DialogHeader>

              <div className="text-[14px]">
                <div>当前选中的 {ids.length} 篇文献中，存在 {unabledPapers.length} 篇无法解析请重选后再试：</div>
                <br />
                {
                  unabledPapers.map((paper) => {
                    return (
                      <div
                        key={paper.paperId}
                        title={paper.paperName ? `${paper.paperName}.pdf` : ''}
                      >《{paper?.extra?.title || paper.paperName}》</div>
                    )
                  })
                }

              </div>
              <DialogFooter>
                <div className="w-full flex flex-row justify-between items-center mt-6">
                  <div className="text-sm">剩余选中文献 {selectedIds?.length} 篇</div>
                  <div className="relative">
                    <Button variant="outline" className="mr-2" onClick={() => {
                      setOpenUnabledPapers(false);
                    }}>取消</Button>
                    <Button disabled={!selectedIds?.length} onClick={() => {
                      setOpenUnabledPapers(false);
                      router.push(`/workflow-creator/selected`);
                    }}>忽略并继续</Button>
                  </div>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog >
        ) : null
      }
    </>
  )
}